import { useMemo } from "react";
import { Box } from "@mui/material";
import { TrajectoryChart } from "@foodpilot/foods";
import { t } from "i18next";

import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useGetAllActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetAllActionPlan";
import { useGetOneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { useGetOneScore } from "src/api/sure-api/score/useGetOneScore";
import type { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import type { Dimensions } from "src/context/OlapScores/useDimensions";
import { EmptyStateCard } from "src/components/EmptyState/EmptyStateCard";

type ScoreTarget = {
    value: number;
    percent: number;
};

type Improvement = "increase" | "decrease";

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const getFilledScoreTarget = (startingValue: number, targetValue: number, scoreTarget: unknown): ScoreTarget | null => {
    if (!isRecord(scoreTarget)) {
        return null;
    }

    const absoluteRaw = scoreTarget["simulationVaryAbsolute"];
    const percentRaw = scoreTarget["simulationVaryPercentage"];
    const absolute = typeof absoluteRaw === "number" ? absoluteRaw : undefined;
    const percent = typeof percentRaw === "number" ? percentRaw : undefined;

    if (absolute === undefined && percent === undefined) {
        return null;
    }

    if (absolute === undefined) {
        const nbPercent = percent as number;
        const growth = (startingValue / 100) * nbPercent;
        const valueFromPercent = targetValue - growth;
        return {
            percent: nbPercent,
            value: valueFromPercent,
        };
    }

    const nbValue = absolute as number;
    const percentFromValue = startingValue > 0 ? 100 - (nbValue / startingValue) * 100 : 0;

    return {
        percent: percentFromValue,
        value: nbValue,
    };
};

const computeGoalValue = (startingValue: number, targetValue: number, scoreTarget: unknown): number | null => {
    const filled = getFilledScoreTarget(startingValue, targetValue, scoreTarget);
    if (!filled) return null;

    const totalDiff = startingValue + filled.value;
    if (!Number.isFinite(totalDiff)) return null;

    return totalDiff;
};

export type TrajectoryChartblockProps = {
    scoreId?: number | null;
    variationId?: number | null;
    height?: number;
    showLegend?: boolean;
};

export const TrajectoryChartblock = (props: TrajectoryChartblockProps) => {
    const { scoreId, variationId, height = 420, showLegend = true } = props;
    const { selectedCompany } = useFoodpilotContext();

    const companyId = selectedCompany?.id ? Number(selectedCompany.id) : undefined;

    const { data: actionPlans, isLoading: isLoadingPlans } = useGetAllActionPlan({
        queryParam: {
            "startSsqPost.organization.id": companyId ?? -1,
        },
    });

    const actionPlanId = actionPlans?.[0]?.id;

    const scoresQuery = useMemo<OlapScoresQuery | undefined>(() => {
        if (!scoreId) return undefined;

        const dimensions: Dimensions = [[["headings"]]];

        return {
            scores: {
                ids: [Number(scoreId)],
                dimensions,
                variations:
                    variationId !== null && variationId !== undefined ?
                        { [Number(scoreId)]: Number(variationId) }
                    :   undefined,
            },
        };
    }, [scoreId, variationId]);

    const { data: actionPlan, isLoading: isLoadingPlan } = useGetOneActionPlan({
        actionPlanId,
        scoresQuery,
    });

    const { score: scoreMeta } = useGetOneScore(Number(scoreId || 0));

    const scoreKey = useMemo(() => (scoreId ? String(Number(scoreId)) : null), [scoreId]);

    const { initialValues, trajectory, goalValue, unit, improvement } = useMemo(() => {
        if (!actionPlan || !scoreId || !scoreKey) {
            return {
                initialValues: [] as { label: string; value: number }[],
                trajectory: [] as { label: string; values: [number | null, number | null] }[],
                goalValue: null as number | null,
                unit: "",
                improvement: (scoreMeta?.improvement === "increase" ? "increase" : "decrease") as Improvement,
            };
        }

        const startScore = actionPlan.startSsqPost?.olapScores?.[scoreKey]?.score?.value;
        const startValue = OlapScoresService.normalizeNumericalScore(startScore, null);

        const startLabelRaw = actionPlan.startSsqPost?.campaign?.label;
        const startLabel =
            typeof startLabelRaw === "string" && startLabelRaw.trim() !== "" ?
                startLabelRaw
            :   t("trajectory_chart.calculatedscore", "Score");

        const init = startValue !== null ? [{ label: startLabel, value: startValue }] : [];

        const withoutByCampaign = new Map<number, number | null>();
        (actionPlan.actionPlanSsqPosts ?? []).forEach((p) => {
            const campaignId = p?.ssqPost?.campaign?.id;
            if (typeof campaignId !== "number") return;
            const raw = p?.ssqPost?.olapScores?.[scoreKey]?.score?.value;
            const v = OlapScoresService.normalizeNumericalScore(raw, null);
            withoutByCampaign.set(campaignId, v);
        });

        const tr = (actionPlan.simulationSsqPosts ?? []).flatMap((p) => {
            const label = p?.campaign?.label;
            const cid = p?.campaign?.id;
            if (typeof label !== "string" || label.trim() === "" || typeof cid !== "number") return [];

            const withRaw = p?.olapScores?.[scoreKey]?.score?.value;
            const withV = OlapScoresService.normalizeNumericalScore(withRaw, null);

            const withoutV = withoutByCampaign.get(cid) ?? null;

            return [{ label, values: [withoutV, withV] as [number | null, number | null] }];
        });

        const lastWithoutPost =
            (actionPlan.actionPlanSsqPosts ?? []).length > 0 ?
                actionPlan.actionPlanSsqPosts[actionPlan.actionPlanSsqPosts.length - 1]?.ssqPost
            :   undefined;

        const targetRaw = lastWithoutPost?.olapScores?.[scoreKey]?.score?.value;
        const targetValue = OlapScoresService.normalizeNumericalScore(targetRaw, null);

        const scoreTargets = Array.isArray(actionPlan.actionPlanScoreTargets) ? actionPlan.actionPlanScoreTargets : [];
        const targetRecord = scoreTargets.find((st) => {
            if (!isRecord(st)) return false;
            const scoreIdRaw = st["scoreId"];
            const variationRaw = st["variationId"];
            const targetVar = typeof variationRaw === "number" ? variationRaw : null;
            const expectedVar = variationId ?? null;
            return typeof scoreIdRaw === "number" && scoreIdRaw === Number(scoreId) && targetVar === expectedVar;
        });

        const goal =
            startValue !== null && targetValue !== null && targetRecord ?
                computeGoalValue(startValue, targetValue, targetRecord)
            :   null;

        const unitValue = actionPlan.startSsqPost?.olapScores?.[scoreKey]?.unit ?? "";

        return {
            initialValues: init,
            trajectory: tr,
            goalValue: goal,
            unit: unitValue,
            improvement: (scoreMeta?.improvement === "increase" ? "increase" : "decrease") as Improvement,
        };
    }, [actionPlan, scoreId, scoreKey, scoreMeta?.improvement, variationId]);

    const isLoading = isLoadingPlans || isLoadingPlan;

    const isEmpty = !isLoading && (initialValues.length === 0 || trajectory.length === 0);

    if (!scoreId || isEmpty) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <EmptyStateCard
                    title={t("cms.trajectory.nodata.title", "Unable to display trajectory chart")}
                    description={t("cms.trajectory.nodata.description", "Data not available for this score")}
                />
            </Box>
        );
    }

    return (
        <Box>
            <TrajectoryChart
                goal={goalValue ?? undefined}
                unit={unit}
                height={`${height}px`}
                initialValues={initialValues}
                improvement={improvement}
                trajectory={trajectory}
                showLegend={showLegend}
                trajectoryOptions={{
                    initialValues: { label: t("trajectory_chart.calculatedscore") },
                    target: { label: t("trajectory_chart.goal"), color: "#3A86FF" },
                    main: { label: t("trajectory_chart.without_action_plan", "Without action plan") },
                    difference: { label: t("trajectory_chart.with_action_plan", "With action plan") },
                }}
                textOptions={{
                    complete: t("trajectory_chart.complete"),
                    between: t("trajectory_chart.between"),
                    and: t("trajectory_chart.and"),
                    withoutActionPlan: t("trajectory_chart.withoutActionPlan", "Without action plan"),
                    goal: t("trajectory_chart.goal"),
                    noObjective: t("trajectory_chart.noobjective"),
                }}
            />
        </Box>
    );
};

export default TrajectoryChartblock;
